import { NextPage } from 'next';
import Head from 'next/head';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import heroImage from '@/assets/escapes/hero.jpg';
import ogThumbnail from '@/assets/escapes/og-thumbnail.gif';
import { HeroLayout } from '@/components/common/Hero/HeroLayout';
import { ETabsColorVariant, ETabsVariant } from '@/components/common/Hero/Tabs';
import { SectionWrapper } from '@/components/common/Section';
import { Categories } from '@/components/escapes/Categories';
import { ExploreTopDestinations } from '@/components/escapes/ExploreTopDestinations';
import { FAQs } from '@/components/escapes/FAQs';
import { HowItWorks } from '@/components/escapes/HowItWorks';
import { ProgressCircle } from '@/components/escapes/ProgressCircle';
import { WhyPeopleLoveOutdoorsy } from '@/components/escapes/WhyPeopleLoveOutdoorsy';
import HeadMeta from '@/components/static-pages/HeadMeta';
import { EHomeSearchWidgetVariant } from '@/components/ui/HomeSearchWidget';
import { ESearchFilters } from '@/constants/searchFilters';
import { useGeoDataMyIp } from '@/hooks/useGeoDataMyIp';
import { setHeaderSearchFilter } from '@/redux/modules/globalHeader';

const Escapes: NextPage = () => {
  const intl = useIntl();

  const dispatch = useDispatch();

  const { data: myIpGeoData } = useGeoDataMyIp();

  const myIPAddress =
    myIpGeoData?.city && myIpGeoData?.state
      ? `${myIpGeoData.city}, ${myIpGeoData.state}`
      : undefined;

  useEffect(() => {
    if (myIPAddress) {
      dispatch(
        setHeaderSearchFilter({
          [ESearchFilters.ADDRESS]: myIPAddress,
        }),
      );
    }
  }, [myIPAddress, dispatch]);

  return (
    <>
      <HeadMeta
        title={intl.formatMessage({
          defaultMessage: 'All-in-one bookings for resorts and delivered RVs | Outdoorsy Escapes',
          id: '+kc7ZN',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'Adventure made easy. Book your Outdoorsy Escape and find your premium RV delivered, set up, and ready for unforgettable moments—just show up and start relaxing.',
          id: '0gRRQp',
        })}
        thumbnailURL={ogThumbnail.src}
        hideOutdoorsyTitle
      />
      <Head>
        <style>{`
          body {
            --page-content-bg-color: #EEECE3;
            --page-header-bg-color: #EEECE3;
            --page-footer-bg-color: #EEECE3;
          }
        `}</style>
      </Head>

      <div className="root">
        <HeroLayout
          headline={
            <FormattedMessage
              defaultMessage="RV delivery to{br}resort destinations."
              id="vk3k1e"
              values={{
                br: <br />,
              }}
            />
          }
          heroImage={heroImage.src}
          subheadline={<FormattedMessage defaultMessage="ALL-IN-ONE-BOOKINGS" id="vcojdt" />}
          tabsColorVariant={ETabsColorVariant.White}
          tabsVariant={ETabsVariant.Escapes}
          variant={EHomeSearchWidgetVariant.Escapes}
        />
        <ProgressCircle />

        <SectionWrapper className="pt-10 pb-5 md:py-10 lg:pt-20">
          <HowItWorks />
        </SectionWrapper>

        <SectionWrapper className="py-5 md:py-10">
          <Categories />
        </SectionWrapper>

        <SectionWrapper className="py-5 md:py-10">
          <ExploreTopDestinations />
        </SectionWrapper>

        <SectionWrapper className="py-5 md:py-10 lg:pb-20">
          <WhyPeopleLoveOutdoorsy />
        </SectionWrapper>

        <SectionWrapper className="pt-5 pb-10 md:py-16 bg-[#FBF8EF]">
          <FAQs />
        </SectionWrapper>
      </div>
    </>
  );
};

Escapes.getInitialProps = async () => {
  return {
    isEscapesPage: true,
  };
};

export default Escapes;
